/**
 * Bankruptcy detection & pedagogical alerts engine.
 *
 * Two trigger paths (combo mode — choice 1c):
 *   1. CESSATION DE PAIEMENTS — treasury < 0 AND equity < 0 for 30 consecutive
 *      days → Tribunal de Commerce prononce la liquidation judiciaire.
 *   2. INSOLVABILITÉ TECHNIQUE — at any monthly close, equity < −10 % × actif total
 *      → immediate liquidation (no grace period).
 *
 * Pedagogical alerts (choice 2b — 3 paliers + AGE) :
 *   - alert_90 (amber)   : streak ≥ 1                     → vigilance, 90 jours estimés
 *   - alert_30 (rose)    : streak ≥ 23 (= 30 − 7)        → danger, AGE proposée
 *   - alert_7  (red)     : streak ≥ 30 − 7 = 23 actually we use 23 for rose, 30-7=23 for rose, … let's use clean thresholds
 *
 * In practice we count `daysUntilLiquidation = 30 − insolvencyStreak`. Paliers :
 *   ≤ 7 days  → 'red'   (AGE button active, last chance)
 *   ≤ 14 days → 'rose'  (warning, AGE button available)
 *   ≤ 30 days → 'amber' (vigilance)
 *   > 30 days OR not insolvent → null
 */

const LIQUIDATION_STREAK_DAYS = 30;
const INSOLVENCY_RATIO_THRESHOLD = -0.10; // equity / actifTotal

/**
 * Compute the current bankruptcy status from a balance-sheet snapshot.
 * Pure function. The caller is responsible for incrementing/resetting
 * `state.insolvencyStreak` and persisting the result.
 *
 * @param {Object} args
 * @param {number} args.treasury        — current cash (after agios)
 * @param {Object} args.balanceSheet    — output of computeBalanceSheet(state)
 * @param {number} args.insolvencyStreak — consecutive days in the danger zone
 * @param {boolean} args.isMonthlyClose — true at the 30-day boundary (allows technical check)
 * @returns {{
 *   inDangerZone: boolean,             // treasury<0 && equity<0
 *   nextStreak: number,                // streak after this tick
 *   daysUntilLiquidation: number|null, // null if not in danger zone
 *   alertStage: 'amber'|'rose'|'red'|null,
 *   liquidationTrigger: null|'cessation'|'technical',
 *   deficit: number,                   // -equity if equity<0, else 0 — pour l'AGE
 *   recapAmount: number,               // 50% of deficit (apport perso minimum)
 * }}
 */
export function computeBankruptcyStatus({ treasury, balanceSheet, insolvencyStreak, isMonthlyClose }) {
  const equity = balanceSheet?.equity ?? 0;
  const actifTotal = balanceSheet?.actif?.total ?? 0;

  const inDangerZone = treasury < 0 && equity < 0;

  // ---- Trigger 2 (technical insolvency) is monthly-only ----
  // Pedagogical fix (27/05/2026) : the legal definition of "insolvabilité"
  // requires the company to be unable to pay its debts as they fall due. A
  // negative equity ratio alone is a serious warning but NOT a sufficient
  // trigger — many real-world companies operate with negative equity while
  // honouring their debts (e.g. just after a big leveraged buyout). We now
  // require BOTH a strongly negative equity ratio AND a treasury < 0 so the
  // player isn't liquidated despite a healthy bank account.
  let liquidationTrigger = null;
  if (
    isMonthlyClose
    && actifTotal > 0
    && equity / actifTotal < INSOLVENCY_RATIO_THRESHOLD
    && treasury < 0
  ) {
    liquidationTrigger = 'technical';
  }

  // ---- Streak update ----
  const nextStreak = inDangerZone ? insolvencyStreak + 1 : 0;

  // ---- Trigger 1 (cessation de paiements) ----
  if (!liquidationTrigger && nextStreak >= LIQUIDATION_STREAK_DAYS) {
    liquidationTrigger = 'cessation';
  }

  // ---- Alert stage (only while still in danger zone, not after trigger) ----
  let alertStage = null;
  let daysUntilLiquidation = null;
  if (inDangerZone && !liquidationTrigger) {
    daysUntilLiquidation = Math.max(0, LIQUIDATION_STREAK_DAYS - nextStreak);
    if (daysUntilLiquidation <= 7) alertStage = 'red';
    else if (daysUntilLiquidation <= 14) alertStage = 'rose';
    else alertStage = 'amber';
  }

  // ---- Recap amount = 50 % of equity deficit ----
  const deficit = equity < 0 ? -equity : 0;
  const recapAmount = Math.round(deficit * 0.5);

  return {
    inDangerZone,
    nextStreak,
    daysUntilLiquidation,
    alertStage,
    liquidationTrigger,
    deficit,
    recapAmount,
  };
}

/**
 * Build the "Bilan & leçons" payload after liquidation. Used by GameOverModal
 * (act 3) AND by `createCompany` reset to seed Maurice's wisdom message.
 *
 * Returns:
 *   {
 *     durationDays, totalRevenue, totalExpenses, finalEquity, finalDebt,
 *     fleetSold, driversDismissed,
 *     causes: [string,string,string]  // 3 reasons ranked by severity
 *     tips:   [string,string,string]  // 3 actionable lessons for the next run
 *   }
 */
export function buildGameOverSummary(state, balanceSheet) {
  const durationDays = state.totalDays || 0;
  const monthlyHistory = state.monthlyHistory || [];
  const totalRevenue = monthlyHistory.reduce((s, m) => s + (m.totalRevenue || 0), 0);
  const totalExpenses = monthlyHistory.reduce((s, m) => s + (m.totalExpenses || 0), 0);
  const finalEquity = balanceSheet?.equity ?? 0;
  const finalDebt = balanceSheet?.passif?.total ?? 0;
  const fleetSold = (state.vehicles || []).length;
  const driversDismissed = (state.drivers || []).length;

  // ---- Detect dominant causes (post-mortem) ----
  const credits = state.credits || [];
  const totalMonthlyCredit = credits.reduce((s, c) => s + (c.monthlyPayment || 0), 0);
  const monthlyExpensesAvg = totalExpenses / Math.max(1, monthlyHistory.length);
  const leasingShare = (state.vehicles || []).filter(v => v.isLeased).length / Math.max(1, fleetSold);
  const lowSatDrivers = (state.drivers || []).filter(d => (d.satisfaction || 0) < 40).length;
  const avgReputation = state.reputation || 0;
  const avgOccupation = (() => {
    if (!fleetSold) return 0;
    const active = (state.vehicles || []).filter(v => v.status === 'active' || v.assignedContractId).length;
    return (active / fleetSold) * 100;
  })();

  // Build candidate causes — each entry = { score, cause, tip }
  const candidates = [
    {
      score: totalMonthlyCredit > monthlyExpensesAvg * 0.30 ? 3 : 0,
      cause: `Charges de crédit trop élevées (${Math.round(totalMonthlyCredit)} €/mois, soit ${Math.round((totalMonthlyCredit / Math.max(1, monthlyExpensesAvg)) * 100)} % des charges).`,
      tip: 'Limitez l\'endettement à 25-30 % des charges mensuelles. Préférez le financement constructeur pour les véhicules (taux plus doux) et négociez l\'effacement partiel chez le banquier en cas de crise.',
    },
    {
      score: leasingShare > 0.7 ? 3 : leasingShare > 0.5 ? 1 : 0,
      cause: `Ratio leasing trop élevé (${Math.round(leasingShare * 100)} % de la flotte louée).`,
      tip: 'Le leasing > 70 % dégrade la réputation et n\'enrichit pas le bilan. Achetez ou financez par crédit hypothécaire pour construire un patrimoine réel.',
    },
    {
      score: avgOccupation < 40 ? 3 : avgOccupation < 60 ? 1 : 0,
      cause: `Flotte sous-utilisée (occupation moyenne ${Math.round(avgOccupation)} %).`,
      tip: 'Avant d\'acheter un nouveau camion, vérifiez que la flotte tourne à ≥ 75 % d\'occupation pendant 30 jours. Sinon, vous payez des charges sans contrepartie.',
    },
    {
      score: lowSatDrivers >= 2 ? 2 : 0,
      cause: `Climat social dégradé (${lowSatDrivers} chauffeur·s en deçà de 40 % de satisfaction).`,
      tip: 'Surveillez la satisfaction des chauffeurs : une démission coûte cher (recrutement + perte de contrat). Anticipez avec des augmentations ciblées et des congés.',
    },
    {
      score: avgReputation < 40 ? 2 : 0,
      cause: `Réputation faible (${Math.round(avgReputation)}/100) — accès aux contrats premium bloqué.`,
      tip: 'La réputation se construit en livrant ponctuellement et en évitant les pénalités. Refusez les contrats trop ambitieux si votre flotte ne suit pas.',
    },
    {
      score: finalDebt > totalRevenue ? 3 : 0,
      cause: `Dette finale (${Math.round(finalDebt)} €) supérieure au chiffre d'affaires total cumulé.`,
      tip: 'Pilotez votre endettement avec la page Capitaux : si la dette dépasse 80 % de l\'actif, freinez les emprunts et concentrez-vous sur l\'augmentation du CA.',
    },
  ];

  // Sort by descending score, keep top 3 with non-zero score
  const ranked = candidates.filter(c => c.score > 0).sort((a, b) => b.score - a.score).slice(0, 3);
  // Fallbacks if not enough specific signals
  const fallbackCause = 'Trésorerie systématiquement négative — vous avez accumulé les charges sans pouvoir les couvrir.';
  const fallbackTip = 'Surveillez la « Runway » sur le Dashboard : si elle tombe sous 3 mois, freinez les dépenses immédiatement (gel des embauches, vente de véhicule, négociation crédit).';

  while (ranked.length < 3) {
    ranked.push({ cause: fallbackCause, tip: fallbackTip });
  }

  return {
    durationDays,
    totalRevenue: Math.round(totalRevenue),
    totalExpenses: Math.round(totalExpenses),
    finalEquity: Math.round(finalEquity),
    finalDebt: Math.round(finalDebt),
    fleetSold,
    driversDismissed,
    causes: ranked.map(r => r.cause),
    tips: ranked.map(r => r.tip),
  };
}

export const BANKRUPTCY_CONSTANTS = {
  LIQUIDATION_STREAK_DAYS,
  INSOLVENCY_RATIO_THRESHOLD,
};
